import React, { useState, useEffect } from 'react';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { CheckCircle, AlertTriangle, XCircle, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';

export const EditableCell = ({ value, field, type = 'text', options = [], onSave, isEditing, onStartEdit }) => {
  const [editValue, setEditValue] = useState(value);
  
  useEffect(() => { setEditValue(value); }, [value]);

  const handleSave = () => {
    onSave(field, type === 'number' ? parseFloat(editValue) || 0 : editValue);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') onStartEdit(null);
  };

  if (!isEditing) {
    return (
      <div onClick={() => onStartEdit(field)} className="cursor-pointer hover:bg-gray-100 px-2 py-1 rounded transition-colors min-h-[28px] flex items-center" data-testid={`cell-${field}`}>
        {type === 'number' && value !== undefined ? (
          field.includes('hourly') || field.includes('cost') || field.includes('rate') || field.includes('revenue') || field.includes('profit') 
            ? `$${value}` : value
        ) : value || '-'}
      </div>
    );
  }

  if (type === 'select') {
    return (
      <Select value={editValue} onValueChange={(v) => { setEditValue(v); onSave(field, v); }}>
        <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <Input type={type} value={editValue} onChange={(e) => setEditValue(e.target.value)} onKeyDown={handleKeyDown} onBlur={handleSave} autoFocus className="h-8 text-sm w-full" data-testid={`edit-${field}`} />
    </div>
  );
};

export const SortableHeader = ({ label, field, sortConfig, onSort, className = '' }) => {
  const isActive = sortConfig.field === field;
  return (
    <th className={`py-3 px-4 text-sm font-semibold text-gray-600 cursor-pointer hover:bg-gray-100 select-none transition-colors ${className}`} onClick={() => onSort(field)} data-testid={`sort-${field}`}>
      <div className="flex items-center gap-1">
        {label}
        {isActive ? (sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 text-gray-400" />}
      </div>
    </th>
  );
};

export const StatusPill = ({ status }) => {
  const colors = { green: 'bg-green-100 text-green-800 border-green-300', yellow: 'bg-yellow-100 text-yellow-800 border-yellow-300', red: 'bg-red-100 text-red-800 border-red-300' };
  const icons = { green: <CheckCircle className="h-3 w-3" />, yellow: <AlertTriangle className="h-3 w-3" />, red: <XCircle className="h-3 w-3" /> };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${colors[status]}`}>
      {icons[status]} {status.toUpperCase()}
    </span>
  );
};

export const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

export const downloadExport = (url) => {
  window.open(url, "_blank", "noopener,noreferrer");
};
